import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Check, Image as ImageIcon, X, Video as VideoIcon } from 'lucide-react';
import { parseQuestionText } from '@/utils/parseQuestionText';
import { splitAllLanguages } from '@/utils/splitAllLanguages';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LANGS = [
  ['sv', '🇸🇪 Svenska'], ['ar', '🇸🇦 العربية'], ['en', '🇬🇧 English'], ['tr', '🇹🇷 Türkçe'], ['fa', '🇮🇷 فارسی'],
  ['so', '🇸🇴 Soomaali'], ['pl', '🇵🇱 Polski'], ['es', '🇪🇸 Español'], ['de', '🇩🇪 Deutsch'],
];
const RTL = ['ar', 'fa'];
const empty = () => ({ raw: '', question: '', options: [], explanation: '' });

const LangSlot = ({ code, label, data, onPaste, onChange }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3" data-testid={`shot-slot-${code}`}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="font-semibold text-sm">{label}</span>
      {data.question && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><Check className="w-3.5 h-3.5" />{data.options.length} alt.</span>}
    </div>
    <textarea value={data.raw} onChange={(e) => onPaste(code, e.target.value)} rows={4} dir={RTL.includes(code) ? 'rtl' : 'ltr'}
      placeholder={RTL.includes(code) ? 'السؤال: ...\nأ. ...\nب. ...\nج. ...\nالإجابة الصحيحة: ج\nالشرح: ...' : 'Fråga: ...\nA. ...\nB. ...\nC. ...\nRätt svar: C\nFörklaring: ...'}
      className="w-full rounded-lg border border-dashed border-emerald-300 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs font-mono" data-testid={`shot-paste-${code}`} />
    {data.question && (
      <div className="space-y-1.5 mt-2" dir={RTL.includes(code) ? 'rtl' : 'ltr'}>
        <textarea value={data.question} onChange={(e) => onChange(code, { question: e.target.value })} rows={2}
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1.5 text-sm" data-testid={`shot-question-${code}`} />
        {data.options.map((o, i) => (
          <input key={i} value={o} onChange={(e) => onChange(code, { options: data.options.map((x, j) => (j === i ? e.target.value : x)) })}
            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1 text-sm" data-testid={`shot-option-${code}-${i}`} />
        ))}
        <textarea value={data.explanation} onChange={(e) => onChange(code, { explanation: e.target.value })} rows={2} placeholder="Förklaring / الشرح"
          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1.5 text-xs" data-testid={`shot-explanation-${code}`} />
      </div>
    )}
  </div>
);

export const ScreenshotQuestionModal = ({ open, onClose, onSuccess, token, initial }) => {
  const [slots, setSlots] = useState(() => Object.fromEntries(LANGS.map(([c]) => [c, empty()])));
  const [correct, setCorrect] = useState(null);
  const [license, setLicense] = useState('b_korkort');
  const [taxiCategory, setTaxiCategory] = useState('sakerhet_och_beteende');
  const [sceneUrl, setSceneUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const [allText, setAllText] = useState('');

  useEffect(() => {
    if (!open) return;
    const q = initial;
    setSlots(Object.fromEntries(LANGS.map(([c]) => [c, q?.question?.[c]
      ? { raw: '', question: q.question[c], options: q.options?.[c] || [], explanation: q.explanation?.[c] || '' } : empty()])));
    setCorrect(q ? q.correct_answer : null);
    setLicense(q?.licenseType || 'b_korkort');
    setTaxiCategory(q?.taxiCategory || 'sakerhet_och_beteende');
    setSceneUrl(q?.image_url || '');
    setVideoUrl(q?.video_url || '');
    setAllText('');
  }, [open, initial]);

  const pasteAll = (text) => {
    setAllText(text);
    const groups = splitAllLanguages(text);
    const codes = Object.keys(groups);
    if (!codes.length) return;
    setSlots((s) => ({ ...s, ...Object.fromEntries(codes.map((c) => [c, { raw: groups[c].raw, question: groups[c].question, options: groups[c].options, explanation: groups[c].explanation }])) }));
    const ref = groups.sv || groups[codes[0]];
    if (ref.correct !== null) setCorrect(ref.correct);
    toast.success(`${codes.length} språk / لغات: ${codes.map((c) => c.toUpperCase()).join(' ')}`);
  };

  const patch = (code, p) => setSlots((s) => ({ ...s, [code]: { ...s[code], ...p } }));

  const paste = (code, raw) => {
    const p = parseQuestionText(raw);
    patch(code, { raw, question: p.question, options: p.options, explanation: p.explanation });
    if (p.correct !== null && (code === 'sv' || correct === null)) setCorrect(p.correct);
  };

  const [uploading, setUploading] = useState(false);
  const uploadScene = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload/image`, fd, { headers });
      if (data.success) { setSceneUrl(data.url); toast.success('Bild uppladdad / تم رفع الصورة'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed / فشل رفع الصورة'); }
    finally { setUploading(false); }
  };

  const uploadVideo = async (file) => {
    if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) return toast.error('MP4, WEBM, MOV');
    const fd = new FormData(); fd.append('file', file);
    setVideoUploading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload/video`, fd, { headers, timeout: 600000 });
      if (data.success) { setVideoUrl(data.url); toast.success('Video uppladdad / تم رفع الفيديو'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed / فشل رفع الفيديو'); }
    finally { setVideoUploading(false); }
  };

  const filled = LANGS.filter(([c]) => slots[c].question);
  const counts = new Set(filled.map(([c]) => slots[c].options.length));
  const ref = slots.sv.question ? slots.sv : filled[0] ? slots[filled[0][0]] : null;

  const save = async () => {
    if (!slots.sv.question) return toast.error('Svenska krävs / السويدية مطلوبة');
    if (counts.size > 1) return toast.error('Antal alternativ skiljer sig mellan språken / عدد الخيارات مختلف بين اللغات');
    if (correct === null || correct >= (slots.sv.options.length || 0)) return toast.error('Välj rätt svar / اختر الإجابة الصحيحة');
    setSaving(true);
    try {
      const pick = (k) => Object.fromEntries(filled.map(([c]) => [c, slots[c][k]]));
      const url = `${API_URL}/api/admin/staged-questions${initial ? `/${initial.id}` : ''}`;
      await axios[initial ? 'put' : 'post'](url, {
        question: pick('question'), options: pick('options'), explanation: pick('explanation'),
        correct_answer: correct, category: 'general', image_url: sceneUrl || null, video_url: videoUrl || null,
        licenseType: license, taxiCategory: license === 'taxi' ? taxiCategory : null,
      }, { headers });
      toast.success('Sparad i dolda frågor ✅ / حُفظ في القسم الخاص');
      onSuccess?.(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="screenshot-question-modal">
        <DialogHeader><DialogTitle>{initial ? 'Redigera fråga / تعديل السؤال' : 'Lägg till fråga (klistra in text) / إضافة سؤال بلصق النص'}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Klistra in frågan per språk (Fråga, A/B/C/D, Rätt svar, Förklaring). Texten sparas exakt som den står. / الصق السؤال لكل لغة، ويُحفظ النص كما هو حرفياً.</p>
        <div className="flex flex-wrap gap-2 items-center">
          {[['b_korkort', '🚗 B-körkort'], ['taxi', '🚕 Taxi']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setLicense(v)} data-testid={`shot-license-${v}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${license === v ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-200'}`}>{l}</button>
          ))}
          {license === 'taxi' && (
            <select value={taxiCategory} onChange={(e) => setTaxiCategory(e.target.value)} className="rounded-lg border px-2 py-1.5 text-sm" data-testid="shot-taxi-category">
              <option value="sakerhet_och_beteende">Säkerhet och beteende</option>
              <option value="lagstiftning">Lagstiftning</option>
            </select>
          )}
        </div>
        <div className="grid sm:grid-cols-[180px_1fr] gap-3">
          <div className="flex flex-col gap-3">
          <label className="relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-3 cursor-pointer min-h-[140px] text-center" data-testid="shot-scene-upload">
            {sceneUrl ? (
              <img src={sceneUrl.startsWith('http') ? sceneUrl : `${API_URL}${sceneUrl}`} alt="" className="max-h-28 rounded object-contain" data-testid="shot-scene-preview" />
            ) : uploading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-600" /> : <ImageIcon className="w-7 h-7 text-emerald-600" />}
            <span className="text-xs font-bold text-emerald-700">{sceneUrl ? 'Byt bild / تغيير الصورة' : 'Bild till frågan / صورة السؤال'}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="shot-scene-input"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadScene(f); }} />
            {sceneUrl && <button type="button" onClick={(e) => { e.preventDefault(); setSceneUrl(''); }} className="absolute top-1.5 end-1.5 rounded-full bg-white p-1 shadow" aria-label="remove" data-testid="shot-scene-remove"><X className="w-3.5 h-3.5 text-slate-500" /></button>}
          </label>
          <label className="relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sky-300 bg-sky-50/50 p-3 cursor-pointer min-h-[110px] text-center" data-testid="shot-video-upload">
            {videoUrl ? (
              <video src={videoUrl.startsWith('http') ? videoUrl : `${API_URL}${videoUrl}`} className="max-h-24 rounded" muted playsInline data-testid="shot-video-preview" />
            ) : videoUploading ? <Loader2 className="w-6 h-6 animate-spin text-sky-600" /> : <VideoIcon className="w-7 h-7 text-sky-600" />}
            <span className="text-xs font-bold text-sky-700">{videoUploading ? 'Laddar upp… / جارٍ الرفع…' : videoUrl ? 'Byt video / تغيير الفيديو' : 'Video till frågan / فيديو السؤال'}</span>
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={videoUploading} data-testid="shot-video-input"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadVideo(f); }} />
            {videoUrl && <button type="button" onClick={(e) => { e.preventDefault(); setVideoUrl(''); }} className="absolute top-1.5 end-1.5 rounded-full bg-white p-1 shadow" aria-label="remove video" data-testid="shot-video-remove"><X className="w-3.5 h-3.5 text-slate-500" /></button>}
          </label>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-600 mb-1">Klistra in alla språk på en gång / الصق كل اللغات دفعة واحدة</p>
            <textarea value={allText} onChange={(e) => pasteAll(e.target.value)} rows={5} data-testid="shot-paste-all"
              placeholder={'🇸🇪 Svenska\nFråga: ...\nA. ...\nRätt svar: A ✅\nFörklaring: ...\n🇸🇦 العربية\nالسؤال: ...'}
              className="w-full h-[140px] rounded-xl border border-emerald-300 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-mono" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {LANGS.map(([c, l]) => <LangSlot key={c} code={c} label={l} data={slots[c]} onPaste={paste} onChange={patch} />)}
        </div>
        {ref && (
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-3" data-testid="shot-correct-picker">
            <p className="text-sm font-bold mb-2">Välj rätt svar / اختر الإجابة الصحيحة</p>
            <div className="grid gap-1.5">
              {ref.options.map((o, i) => (
                <button key={i} type="button" onClick={() => setCorrect(i)} data-testid={`shot-correct-${i}`}
                  className={`text-start rounded-lg px-3 py-2 text-sm border ${correct === i ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white dark:bg-slate-800 border-slate-200'}`}>
                  {i + 1}. {o}
                </button>
              ))}
            </div>
            {counts.size > 1 && <p className="mt-2 text-xs text-rose-600">⚠ Antal alternativ skiljer sig mellan språken / عدد الخيارات مختلف بين اللغات</p>}
          </div>
        )}
        <div className="flex justify-between items-center gap-2">
          <span className="text-xs text-slate-500">{filled.length}/9</span>
          <Button onClick={save} disabled={saving || uploading || videoUploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="shot-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin me-2" />}Spara fråga / حفظ السؤال
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
